import React, { useState, useEffect } from 'react';
import { IconCheck, IconExpand, IconX } from './Icons';
import { useLanguage } from '../context/LanguageContext';
import useScrollReveal from '../hooks/useScrollReveal';
import './CaseStudies.css';

const CaseStudies = () => {
  const { t } = useLanguage();
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    if (lightboxImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);

  const caseStudies = [
    {
      id: 3,
      client: t.caseStudies.case1Client,
      subtitle: t.caseStudies.case1Subtitle,
      badge: t.caseStudies.case1Badge,
      highlight: t.caseStudies.case1Highlight,
      description: t.caseStudies.case1Desc,
      image: '/img/3..png',
      changes: [t.caseStudies.changePortfolio, t.caseStudies.changeNegativeKW, t.caseStudies.changeConversion, t.caseStudies.changeRetargeting, t.caseStudies.changeLaunch, t.caseStudies.changeListing]
    },
    {
      id: 4,
      client: t.caseStudies.case2Client,
      subtitle: t.caseStudies.case2Subtitle,
      badge: t.caseStudies.case2Badge,
      highlight: t.caseStudies.case2Highlight,
      description: t.caseStudies.case2Desc,
      image: '/img/4..png',
      changes: [t.caseStudies.changePortfolio, t.caseStudies.changeNegativeKW, t.caseStudies.changeConversion, t.caseStudies.changeRetargeting, t.caseStudies.changeLaunch, t.caseStudies.changeListing]
    },
    {
      id: 5,
      client: t.caseStudies.case3Client,
      subtitle: t.caseStudies.case3Subtitle,
      badge: t.caseStudies.case3Badge,
      highlight: t.caseStudies.case3Highlight,
      description: t.caseStudies.case3Desc,
      image: '/img/5..png',
      changes: [t.caseStudies.changePortfolioNew, t.caseStudies.changeNegativeKW, t.caseStudies.changeConversion, t.caseStudies.changeRetargeting, t.caseStudies.changeLaunch, t.caseStudies.changeListing]
    },
    {
      id: 1,
      client: t.caseStudies.case4Client,
      subtitle: t.caseStudies.case4Subtitle,
      badge: t.caseStudies.case4Badge,
      highlight: t.caseStudies.case4Highlight,
      description: t.caseStudies.case4Desc,
      image: '/img/1..png',
      changes: [t.caseStudies.changePortfolio, t.caseStudies.changeNegativeKW, t.caseStudies.changeTopPerformer]
    },
    {
      id: 2,
      client: t.caseStudies.case5Client,
      subtitle: t.caseStudies.case5Subtitle,
      badge: t.caseStudies.case5Badge,
      highlight: t.caseStudies.case5Highlight,
      description: t.caseStudies.case5Desc,
      image: '/img/2..png',
      changes: [t.caseStudies.changePortfolio, t.caseStudies.changeNegativeKW, t.caseStudies.changeConversion, t.caseStudies.changeTopPerformer]
    }
  ];

  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const listReveal = useScrollReveal({ threshold: 0.05 });

  return (
    <section className="case-studies-fox">
      <div className="case-studies-fox__container">
        <div ref={headerReveal.ref} className={`case-studies-fox__header reveal ${headerReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <span className="case-studies-fox__label">{t.caseStudies.label}</span>
          <h2 className="case-studies-fox__title">
            {t.caseStudies.title} <span className="case-studies-fox__title-gradient">{t.caseStudies.titleHighlight}</span>
          </h2>
          <p className="case-studies-fox__subtitle">{t.caseStudies.subtitle}</p>
        </div>

        <div ref={listReveal.ref} className={`case-studies-fox__list reveal--fade ${listReveal.isRevealed ? 'reveal--visible' : ''}`}>
          {caseStudies.map((study) => (
            <div key={study.id} className="case-studies-fox__card">
              <div className="case-studies-fox__card-content">
                <div className="case-studies-fox__text-content">
                  <div className="case-studies-fox__card-top">
                    <span className="case-studies-fox__badge">{study.badge}</span>
                  </div>
                  <h3 className="case-studies-fox__client">{study.client}</h3>
                  <span className="case-studies-fox__subtitle-text">{study.subtitle}</span>
                  <p className="case-studies-fox__highlight">{study.highlight}</p>
                  {study.description && <p className="case-studies-fox__description">{study.description}</p>}
                  <div className="case-studies-fox__changes">
                    <span className="case-studies-fox__changes-title">{t.caseStudies.changesTitle}</span>
                    <ul className="case-studies-fox__changes-list">
                      {study.changes.slice(0, 4).map((change, idx) => (
                        <li key={idx} className="case-studies-fox__change-item">
                          <span className="case-studies-fox__change-icon"><IconCheck size={14} /></span>
                          {change}
                        </li>
                      ))}
                      {study.changes.length > 4 && (
                        <li className="case-studies-fox__change-item case-studies-fox__change-item--more">
                          +{study.changes.length - 4} {t.caseStudies.moreChanges}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="case-studies-fox__image-container" onClick={() => setLightboxImage(study.image)}>
                  <img src={study.image} alt={`Case Study ${study.client}`} className="case-studies-fox__image" width={400} height={300} loading="lazy" decoding="async" />
                  <div className="case-studies-fox__image-overlay">
                    <IconExpand size={20} />
                    <span className="case-studies-fox__image-hint">{t.caseStudies.clickToEnlarge}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {lightboxImage && (
        <div className="case-studies-fox__lightbox" onClick={() => setLightboxImage(null)}>
          <button className="case-studies-fox__lightbox-close" onClick={() => setLightboxImage(null)} aria-label={t.caseStudies.closeLightbox}>
            <IconX size={24} />
          </button>
          <img src={lightboxImage} alt="Case Study Preview" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
};

export default CaseStudies;
